import { isMajesticTopper } from './majesticTopper'

/**
 * Utility functions for filtering toppers based on bed type and size
 */

interface TopperModel {
  width_cm: number | null
  is_split: boolean
  reclined_side?: string | null
  three_d_model: { id: number, file_url: string, preview_url?: string | null }
}

/**
 * Generic topper interface that works with both Option type and raw API response
 */
interface TopperLike {
  id: number | string
  title?: string
  slug?: string | null
  slug_en?: string | null
  three_d_models?: Array<{
    width_cm: number | null
    is_split: boolean
    reclined_side?: string | null
    three_d_model: { id: number, file_url: string, preview_url?: string | null }
  }>
}

/** Majestic toppers are only offered on beds at least 150 cm wide. */
export function isTopperAvailableForWidth(topper: TopperLike, selectedWidth: number): boolean {
  return selectedWidth >= 150 || !isMajesticTopper(topper.slug, topper.slug_en)
}

export function filterToppersForWidth<T extends TopperLike>(toppers: T[], selectedWidth: number): T[] {
  return toppers.filter((topper) => isTopperAvailableForWidth(topper, selectedWidth))
}

function isReclinedSide(side: string | null | undefined, target: 'left' | 'right'): boolean {
  if (!side) return false
  return String(side).toLowerCase() === target
}

function hasReclinedSide(side: string | null | undefined): boolean {
  return !!side
}

/**
 * Check if a topper is valid for an adjustable bed at the given size
 * 
 * Rules for adjustable beds (ALL sizes):
 * - Split toppers work with adjustable beds >= 150cm
 * - For beds <150cm: Must have reclined models at full width (will render as single topper)
 * - For beds >=150cm: Must have split toppers OR reclined models (left and right at half width, will render as two halves)
 */
export function isTopperValidForAdjustableBed(topper: TopperLike, selectedWidth: number): boolean {
  const models = (topper.three_d_models || []) as TopperModel[]
  const halfWidth = Math.floor(selectedWidth / 2)
  const isSingleBed = selectedWidth < 150
  
  // Split toppers work with adjustable beds >= 150cm
  const hasSplitModel = models.some((m) => 
    m.width_cm === selectedWidth && 
    m.is_split && 
    m.three_d_model?.file_url
  )
  
  if (hasSplitModel) {
    return true
  }
  
  if (isSingleBed) {
    // For single beds (<150cm): Need reclined models at full width (but will render as single topper)
    const leftModel = models.find((m) => 
      m.width_cm === selectedWidth && 
      !m.is_split && 
      isReclinedSide(m.reclined_side, 'left')
    )
    const rightModel = models.find((m) => 
      m.width_cm === selectedWidth && 
      !m.is_split && 
      isReclinedSide(m.reclined_side, 'right')
    )
    // For beds <150cm, we need reclined models but will use just one of them (not both)
    // So we check if at least one reclined model exists
    return !!(leftModel?.three_d_model?.file_url || rightModel?.three_d_model?.file_url)
  } else {
    // For larger beds (>=150cm): For non-split toppers, need both left and right reclined models at half width
    const leftModel = models.find((m) => 
      m.width_cm === halfWidth && 
      !m.is_split && 
      isReclinedSide(m.reclined_side, 'left')
    )
    const rightModel = models.find((m) => 
      m.width_cm === halfWidth && 
      !m.is_split && 
      isReclinedSide(m.reclined_side, 'right')
    )
    return !!(leftModel?.three_d_model?.file_url && rightModel?.three_d_model?.file_url)
  }
}

/**
 * Filter toppers for adjustable beds
 */
export function filterToppersForAdjustableBed<T extends TopperLike>(toppers: T[], selectedWidth: number): T[] {
  return toppers.filter(topper => isTopperValidForAdjustableBed(topper, selectedWidth))
}

/**
 * Select the best matching model from a topper's available models
 * 
 * For adjustable beds:
 * - Beds <150cm: Prefer reclined models (left or right) at full width
 * - Beds >=150cm: Prefer split toppers, otherwise left reclined at half width (single-topper fallback)
 * 
 * For non-adjustable beds:
 * - Prefer non-split, non-reclined models
 */
export function selectBestTopperModel(
  models: TopperModel[],
  selectedWidth: number,
  isAdjustable: boolean,
  fallbackModel?: { id: number, file_url: string, preview_url?: string | null } | null
): { id: number, file_url: string, preview_url?: string | null } | null {
  if (!models.length) {
    return isAdjustable ? null : (fallbackModel || null)
  }

  if (isAdjustable && selectedWidth) {
    if (selectedWidth < 150) {
      const reclined = models.find(
        (m) => m.width_cm === selectedWidth && !m.is_split && hasReclinedSide(m.reclined_side)
      )
      if (reclined?.three_d_model?.file_url) return reclined.three_d_model
    } else {
      const split = models.find((m) => m.width_cm === selectedWidth && m.is_split)
      if (split?.three_d_model?.file_url) return split.three_d_model

      const halfWidth = Math.floor(selectedWidth / 2)
      const leftHalf = models.find(
        (m) => m.width_cm === halfWidth && !m.is_split && isReclinedSide(m.reclined_side, 'left')
      )
      if (leftHalf?.three_d_model?.file_url) return leftHalf.three_d_model

      if (selectedWidth === 150) {
        const leftFull = models.find(
          (m) => m.width_cm === selectedWidth && !m.is_split && isReclinedSide(m.reclined_side, 'left')
        )
        if (leftFull?.three_d_model?.file_url) return leftFull.three_d_model
      }

      const anyReclined = models.find(
        (m) =>
          hasReclinedSide(m.reclined_side) &&
          !m.is_split &&
          m.three_d_model?.file_url &&
          (m.width_cm === selectedWidth || m.width_cm === halfWidth)
      )
      if (anyReclined) return anyReclined.three_d_model
    }

    // Never fall back to flat models on adjustable beds
    return null
  }

  if (selectedWidth) {
    const flat = models.find(
      (m) => m.width_cm === selectedWidth && !m.is_split && !hasReclinedSide(m.reclined_side)
    )
    if (flat?.three_d_model?.file_url) return flat.three_d_model

    const anyAtWidth = models.find((m) => m.width_cm === selectedWidth && !m.is_split)
    if (anyAtWidth?.three_d_model?.file_url) return anyAtWidth.three_d_model

    const anyWidth = models.find((m) => m.width_cm === selectedWidth)
    if (anyWidth?.three_d_model?.file_url) return anyWidth.three_d_model
  }

  const nonReclined = models.find((m) => !hasReclinedSide(m.reclined_side))
  if (nonReclined?.three_d_model?.file_url) return nonReclined.three_d_model

  if (fallbackModel) return fallbackModel
  return models[0]?.three_d_model || null
}
